// UserProfile.tsx
import React, { useState, useEffect } from 'react';
import { useUser } from 'util/UserContext';
import { useParams } from 'react-router-dom';
import api from 'config/axiosConfig';
import Feed from 'components/Entities/Feed';
import UserProfileSaved from './UserProfileSaved';
import UserProfileDrafts from './UserProfileDrafts';
import UserProfilePlate from './UserProfilePlate';
import { User } from 'util/constants';

// Import the new reusable scrollable tabs
import ScrollableTabsList from 'ui/ScrollableTabsList';
import { capitalizeFirstLetter } from 'util/textUtils';
import { setDocumentTitle, setEntityOg } from 'util/ogMetadataUtils';
import { AxiosError } from 'axios';
import ServerErrorPage from 'components/ResponsePages/500ServerErrorPage';
import NotFoundPage from 'components/ResponsePages/404NotFoundPage';
import ScrollHideContainer from 'ui/ScrollHideContainer';
import { apiRoutes } from "util/apiRoutes";
import { getDraftUiStrings, resolveDraftLocale } from "i18n/draftUi";

const UserProfile: React.FC = () => {
  const { currentUser, awaitingUser } = useUser();
  const { username, tab } = useParams<{ username: string; tab?: string }>();

  const [selectedTab, setSelectedTab] = useState<string>(tab || 'overview');
  const [usernameActive, setUsernameActive] = useState<User | null>(null);
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [isDetailPanelOpen, setIsDetailPanelOpen] = useState<boolean>(false);
  const draftUi = getDraftUiStrings(resolveDraftLocale());

  setDocumentTitle(`User - ${username} - ${capitalizeFirstLetter(selectedTab)}`);

  useEffect(() => {
    if (tab) {
      setSelectedTab(tab);
    } else {
      setSelectedTab('overview');
    }
  }, [username, tab]);

  useEffect(() => {
    const fetchUsername = async () => {
      try {
        const response = await api.get<User>(apiRoutes.user.byUsername(username || ""));
        setUsernameActive(response.data);
        // Update OG meta for user page; issues have no user, but user pages do
        setEntityOg({
          entityType: 'user',
          entity: response.data as any,
          urlPath: `/user/${encodeURIComponent(String(username))}`,
        });
        setLoading(false);
      } catch (error) {
        setLoading(false);
        if (error instanceof AxiosError && error.response?.status === 404) {
          setError(`404: User with username: ${username} does not exist or is inactive.`);
        } else {
          console.error(`Error fetching user with username: ${username}`, error);
          setError(`Something went wrong fetching user with username: ${username}`);
        }
      }
    };
    fetchUsername();
  }, [username]);

  if ((!loading && !usernameActive) || (username === '[deleted]')) {
    return <NotFoundPage displayStyle={`page`} errorMessage={`Sorry, no one's using that username.`} errorSubtext={`The account may be banned or inactive, or the username is incorrect.`} buttonText={`Look Somewhere Else`} />
  }
  
  if (!loading && error) {
    return error.match(/404/) ? <NotFoundPage displayStyle={`page`} errorMessage={`Sorry, no one's using that username.`} errorSubtext={`The account may be banned or inactive, or the username is incorrect.`} buttonText={`Look Somewhere Else`} /> : <ServerErrorPage />
  }


  // Build the array of tabs to pass into ScrollableTabsList
  const tabsData = [
    {
      label: 'Overview',
      route: `/user/${username}/overview`,
      value: 'overview',
    },
    {
      label: 'Posts',
      route: `/user/${username}/posts`,
      value: 'posts',
    },
    {
      label: 'Comments',
      route: `/user/${username}/comments`,
      value: 'comments',
    },
    {
      label: 'Feedback',
      route: `/user/${username}/feedback`,
      value: 'feedback',
    },
  ];

  const handlePanelOpenChange = (open: boolean) => {
      setIsDetailPanelOpen(open);
  };


  // If it's the current user's own profile, add private tabs.
  if (username === currentUser?.username) {
    tabsData.push({
      label: draftUi.draftsTab,
      route: `/user/${username}/drafts`,
      value: 'drafts',
    });
    tabsData.push({
      label: 'Saved',
      route: `/user/${username}/saved`,
      value: 'saved',
    });
  }

  if (!awaitingUser && username !== currentUser?.username && (selectedTab === 'saved' || selectedTab === 'drafts')) {
    window.location.href = '.';
  }

  return (
    <div className="py-0 relative">
      {/* User Profile Plate */}
      {usernameActive && 
        <UserProfilePlate user={usernameActive}
          isDetailPanelOpen={
            usernameActive.about && typeof usernameActive.about === 'object' 
            ? Object.keys(usernameActive.about).some(key => key.startsWith('about_alignment_answer')) && isDetailPanelOpen
            : isDetailPanelOpen
          }
          onOpenPanelChange={handlePanelOpenChange}
        />}

      <ScrollHideContainer 
        className="z-50 max-w-5xl bg-eoe-bg dark:bg-eoe-dark-bg"
        hideOffset={57}
        threshold={56}
      >
        {/* Reusable Scrollable Tabs */}
        <div className="p-2">
          <ScrollableTabsList
          key={`${username}-tabs`} 
          pageTitle={`User - ${username}`}
          tabs={tabsData}
          selectedTab={selectedTab}
            onTabChange={() => {}}
          />
        </div>
      </ScrollHideContainer>

      {/* Tabs Content */}
      <div className="py-0 relative">
        {selectedTab === 'overview' && (
          <Feed
            username={username}
            entityTypes={[
              'impact',
              'teabate',
              'solution',
              'comment',
              'feedback',
              'crossposts',
              'action',
            ]}
            hideTabList
            actorId={usernameActive?.id}
            isActorPage={true}
          />
        )}
        {selectedTab === 'posts' && (
          <Feed
            username={username}
            entityTypes={['impact', 'teabate', 'solution', 'crosspost', 'action']}
            hideTabList
            actorId={usernameActive?.id}
            isActorPage={true}
          />
        )}
        {selectedTab === 'comments' && (
          <Feed username={username} entityTypes={['comment']} hideTabList isActorPage={true} />
        )}
        {selectedTab === 'feedback' && (
          <Feed username={username} entityTypes={['feedback']} hideTabList isActorPage={true} />
        )}
        {username === currentUser?.username && selectedTab === 'saved' && (
          <UserProfileSaved />
        )}
        {username === currentUser?.username && selectedTab === 'drafts' && (
          <UserProfileDrafts />
        )}
      </div>
    </div>
  );
};

export default UserProfile;
